/**
 * Import d'un relevé CODA : du fichier `.BC2` aux écritures en base.
 *
 * L'import se fait en **deux temps**, et cette séparation est le cœur du
 * module :
 *
 * 1. `planImport` ne lit que. Il rend un plan complet : ce qui serait créé, ce
 *    qui serait rapproché, ce qui partirait en file manuelle, et pourquoi.
 * 2. `commitImport` écrit ce plan.
 *
 * On peut donc montrer à l'administration ce que l'import va faire **avant**
 * qu'il le fasse. Sur une reprise de rapprochement à zéro, après un an sans
 * vérification, c'est la différence entre un outil qu'on ose lancer et un outil
 * qu'on n'ose pas.
 *
 * ## Idempotence, à deux niveaux
 *
 * Télécharger sept fichiers par semaine et en réimporter un par mégarde est le
 * geste le plus naturel du monde. Deux garde-fous, tous deux vérifiés sur les
 * relevés réels :
 *
 * - **l'empreinte du contenu** (`payment_imports.content_sha256`) : le même
 *   fichier deux fois est reconnu et refusé, quel que soit son nom ;
 * - **la référence bancaire** (`payments.external_ref`, unique par source) :
 *   sur 800 mouvements répartis dans 22 fichiers, les 800 références sont
 *   distinctes, aucune réutilisation. ING y met un compteur global.
 *
 * Le second rattrape ce que le premier laisse passer : deux fichiers différents
 * qui se chevauchent, ou un fichier réexporté par la banque.
 *
 * ## Et un troisième, depuis le 27 septembre 2026 : ce que l'export CSV a écrit
 *
 * Les deux premiers ne voient que les lignes CODA. Le 27/09/2026, dix-sept
 * relevés importés sur une période que l'export CSV couvrait déjà ont écrit
 * **48 mouvements une seconde fois** (6 838,63 €), dont un virement de
 * 173,00 € qui avait déjà soldé sa facture. Le relevé apparie donc désormais
 * ses mouvements avec les lignes CSV encore non reliées de sa période
 * (`jumeaux.ts`) : un jumeau sûr est **relié** — la ligne CSV reçoit la
 * référence du relevé, rien n'est réécrit — ; un jumeau douteux (même forme,
 * autre communication) est écrit et **signalé**. La base refuse ensuite toute
 * ligne CODA dont la référence est déjà reliée.
 */

import { decodeCodaBytes, incomingPayments, nonTransfers, outgoingTransfers, parseCoda, type CodaMovement } from './coda';
import { aEcrire, cleDeMouvement, parseCsvIng } from './csvIng';
import { buildInvoiceIndex, planBatch, summarize, allLegacyLookups, invoiceDigits, type InvoiceRef, type MatchPlan, type BatchSummary } from './matching';
import { classifyOutgoing } from './refundMatching';
import { apparierAvecLExportCsv, type Communications, type LigneCsv, type Sens } from './jumeaux';

/**
 * D'ou vient le fait bancaire.
 *
 * `bank` est le CODA, le format officiel de la banque. `bank_csv` est l'export
 * de la banque en ligne, qui sert a combler les trous — memes tables, meme
 * journal, provenance ecrite. Voir `csvIng.ts`.
 */
export type SourceBancaire = 'bank' | 'bank_csv';

/** Une ligne de `payments` telle que l'import la crée. */
export interface PaymentRow {
  source: SourceBancaire;
  external_ref: string;
  amount_cents: number;
  value_date: string;
  /**
   * La date comptable de la banque, quand le fichier la porte. Elle choisit
   * l'exercice du mouvement ; la date valeur reste celle du rapprochement.
   * Voir `CodaMovement.entryDate`.
   */
  booking_date: string | null;
  communication_raw: string | null;
  communication_kind: 'structured' | 'legacy' | 'unstructured' | null;
  structured_digits: string | null;
  legacy_invoice_ref: string | null;
  possibly_truncated: boolean;
  counterparty_iban: string | null;
  counterparty_name: string | null;
  /**
   * Le libellé complet du relevé, enregistrements CODA 31 et 32.
   *
   * Le champ communication fait 53 caractères et tronque au-delà. Cas réel :
   * `CDV-NDSC-2601965/Monica William 95 euros-92 euros not` — la phrase de la
   * mère s'arrête au milieu d'un mot, sur un paiement de 3,00 € pour une
   * facture de 95,00 €. Ce qu'elle explique est exactement ce qu'il faut
   * savoir, et c'est le texte libre qui le porte.
   *
   * Sur les 719 paiements des relevés réels, les 719 ont un texte libre.
   */
  free_text: string | null;
}

/** Une ligne de `payment_allocations`, avant que l'id du paiement soit connu. */
export interface AllocationRow {
  /** Résolu en `payment_id` au moment de l'écriture. */
  externalRef: string;
  amount_cents: number;
  target: 'invoice' | 'out_of_scope';
  /** `null` hors périmètre : l'argent ne va sur aucune facture d'ici. */
  invoice_id: string | null;
  method: 'structured' | 'legacy_reference' | 'manual';
  /** Le motif, en clair, pour que la file puisse l'afficher et le défaire. */
  notes?: string | null;
}

/** Une ligne de `refunds` : un virement sortant qui cite une facture ou une note. */
export interface RefundRow {
  source: SourceBancaire;
  external_ref: string;
  amount_cents: number;
  value_date: string;
  /** La date comptable de la banque, comme pour un paiement reçu. */
  booking_date: string | null;
  communication_raw: string;
  legacy_invoice_ref: string | null;
  invoice_id: string | null;
  possibly_truncated: boolean;
  creditNoteNumbers: string[];
  /**
   * La nature de la communication, comme pour un paiement recu.
   *
   * Manquait, et son absence privait l'ecran du seul indice qui distingue une
   * communication structuree — donc une demande de remboursement que NOUS avons
   * emise — d'une annotation manuelle.
   */
  communication_kind: 'structured' | 'legacy' | 'unstructured' | null;
  structured_digits: string | null;
  /**
   * Le libelle complet du releve, enregistrements CODA 31 et 32.
   *
   * **Le manque le plus couteux du sortant.** Le champ communication fait 53
   * caracteres et coupe au-dela ; le numero de note s'ecrivant APRES celui de
   * la facture, c'est lui qui saute — 11 remboursements sur 45. Cote entrant,
   * conserver ce texte est exactement ce qui sauve ces cas.
   *
   * Mesure sur le fichier d'un an : les 1 758 virements sortants ont TOUS un
   * texte libre, et tous un IBAN de beneficiaire. L'information etait la, elle
   * n'etait simplement jamais ecrite.
   */
  free_text: string | null;
  /*
    La contrepartie d'un virement SORTANT est le bénéficiaire.

    Elle ne voyageait pas : `refunds` n'avait aucune colonne pour l'accueillir,
    donc « chercher une dépense par son numéro de compte » ne pouvait rien
    trouver. Les deux colonnes ont été ajoutées le 6 septembre 2026 ; les 45
    lignes antérieures restent sans IBAN, les prochaines l'auront.
  */
  counterparty_iban: string | null;
  counterparty_name: string | null;
  /**
   * La categorie hors perimetre familles reconnue A LA FORME, ou `null`.
   *
   * Renseignee, elle fait poser une allocation `out_of_scope` a l'ecriture : le
   * mouvement est compte, classe, et sorti de la file — mais jamais rattache a
   * une famille, et jamais efface.
   */
  out_of_scope: string | null;
}

export interface ImportPlan {
  filename: string;
  contentSha256: string;
  /** `coda` : un releve. `csv` : un export de la banque en ligne. */
  format: 'coda' | 'csv';
  source: SourceBancaire;
  /** La periode couverte. Un CSV en couvre une ; un CODA porte un numero. */
  periodFrom: string | null;
  periodTo: string | null;
  /**
   * Mouvements du fichier que la base porte deja, reconnus au comptage.
   *
   * Propre au CSV : un CODA se reconnait a sa reference bancaire, un CSV se
   * compte. Voir `aEcrire` et `mouvements_bancaires_connus`.
   */
  dejaConnus: number;
  /** Renseigné quand ce fichier a déjà été importé : rien ne sera écrit. */
  alreadyImportedAs: { id: string; filename: string; createdAt: string } | null;
  accountIban: string;
  statementNumber: string;
  statementDate: string | null;
  /** Écarts de totaux de contrôle relevés par le parseur. */
  problems: string[];
  movementCount: number;
  /** Paiements à créer. */
  payments: PaymentRow[];
  /** Références déjà présentes en base, donc ignorées. */
  duplicateRefs: string[];
  /** Remboursements déjà présents en base, donc ignorés. Symétrique de `duplicateRefs`. */
  duplicateRefundRefs: string[];
  /** Ce qu'une relecture vient ajouter aux lignes déjà en base. Voir `RefundCompletion`. */
  refundCompletions: RefundCompletion[];
  /**
   * Mouvements du relevé que l'export CSV a déjà écrits : ils ne seront pas
   * réécrits, la ligne CSV recevra la référence du relevé. Vide pour un CSV.
   */
  reunisAuCsv: ReunionAuCsv[];
  /**
   * Même forme qu'une ligne de l'export CSV, mais une autre communication :
   * écrits quand même — deux virements distincts peuvent se ressembler à ce
   * point —, et signalés pour qu'un humain tranche.
   */
  doublonsProbables: DoublonProbable[];
  /**
   * Opérations hors virement du relevé (cartes, frais, domiciliations…) que
   * l'export CSV a déjà écrites, reconnues au comptage : pas réécrites. Vide
   * pour un CSV. Voir `planLesOperationsHorsVirement`.
   */
  horsVirementDejaEcrits: string[];
  /**
   * Opérations hors virement de 0,00 € : ING en met pour information. Elles ne
   * bougent pas le solde, et la base refuse un mouvement nul
   * (`payments_amount_positive`) — le 29/09/2026, l'une d'elles a fait refuser
   * tout l'extrait 267. Comptées, jamais écrites.
   */
  horsVirementNuls: string[];
  /** Les références des opérations hors virement que ce plan écrit, entrées et sorties. */
  horsVirementAEcrire: string[];
  /**
   * Les soldes que le relevé annonce, en centimes signés. `null` pour un CSV,
   * qui n'en porte pas. De quoi comparer à l'œil le solde de l'extrait et celui
   * de CDV.
   */
  soldes: { ancienCents: number; nouveauCents: number } | null;
  /** Allocations proposées, pour les paiements nouveaux comme pour ceux non encore affectés. */
  allocations: AllocationRow[];
  refunds: RefundRow[];
  /** Le détail par paiement, pour l'écran de contrôle. */
  matchPlans: MatchPlan[];
  summary: BatchSummary;
}

/**
 * Ce qu'un relevé relu vient AJOUTER à une ligne déjà en base.
 *
 * ## Pourquoi cela existe
 *
 * Demandé le 20 septembre 2026 : « relire les CODA pour récupérer le compte du
 * bénéficiaire ». C'était impossible. L'import est idempotent sur
 * `(source, external_ref)` et se contente de COMPTER ce qu'il connaît déjà :
 * relire le même fichier n'écrivait rien, quelle que soit la richesse de ce
 * qu'il contenait.
 *
 * Mesuré le 20 septembre 2026 : sur les 46 remboursements venus des CODA, **34
 * n'ont ni compte de contrepartie, ni nom, ni texte libre** — tous à partir du
 * relevé du 15 janvier 2026, alors que les 12 antérieurs les ont. Les 13 613
 * paiements reçus des mêmes fichiers, eux, ont tous leur compte. Ce qui manque
 * manque donc DANS les fichiers, à un endroit qu'on ne peut atteindre qu'en les
 * relisant.
 *
 * ## Ce qu'une complétion ne fait jamais
 *
 * Elle ne REMPLACE rien. Seuls les champs vides en base sont écrits, et
 * seulement quand le relevé en porte un non vide. Un relevé rectificatif qui
 * dirait autre chose qu'un premier ne doit pas pouvoir réécrire l'histoire d'un
 * mouvement sans que personne ne le voie ; et sans cette règle, relire un
 * fichier plus pauvre EFFACERAIT ce qu'un fichier plus riche avait apporté.
 */
export interface RefundCompletion {
  id: string;
  external_ref: string;
  counterparty_iban?: string;
  counterparty_name?: string;
  free_text?: string;
}

/** Un mouvement du relevé que l'export CSV a déjà écrit. */
export interface ReunionAuCsv {
  sens: Sens;
  /** La référence bancaire du relevé, posée sur la ligne CSV. */
  externalRef: string;
  /** La ligne de l'export CSV qui décrit ce mouvement. */
  csvId: string;
  valueDate: string;
  amountCents: number;
  /** Ce que lit le relevé : la base le recompare à la ligne CSV avant de relier. */
  counterpartyIban: string | null;
  /** Complètent la ligne CSV là où elle est vide — jamais au-delà. */
  counterpartyName: string | null;
  freeText: string | null;
  communications: Exclude<Communications, 'differentes'>;
  communication: string | null;
}

/** Même forme qu'une ligne CSV, autre communication : écrit, et à vérifier. */
export interface DoublonProbable {
  sens: Sens;
  externalRef: string;
  csvId: string;
  valueDate: string;
  amountCents: number;
  communicationReleve: string | null;
  communicationCsv: string | null;
}

/** Ce que `relier_au_coda` reçoit : la ligne CSV, et ce que le relevé en dit. */
export interface LienCoda {
  sens: Sens;
  id: string;
  coda_ref: string;
  value_date: string;
  amount_cents: number;
  counterparty_iban: string | null;
  counterparty_name: string | null;
  free_text: string | null;
}

export interface ImportReport {
  importId: string | null;
  alreadyImported: boolean;
  paymentsCreated: number;
  duplicatesSkipped: number;
  /** Remboursements ignores parce que deja en base. */
  refundDuplicatesSkipped: number;
  allocationsCreated: number;
  refundsCreated: number;
  /** Remboursements deja en base qu'une relecture a completes. */
  refundsCompleted: number;
  /** Mouvements deja ecrits par l'export CSV, relies au releve au lieu d'etre reecrits. */
  reunisAuCsv: number;
  summary: BatchSummary;
}

/**
 * Tout ce dont l'import a besoin de la base, et rien de plus.
 *
 * L'interface existe pour que la logique d'import se teste sans base de
 * données. L'implémentation Supabase est dans `importGatewaySupabase`.
 */
export interface ImportGateway {
  findImportByHash(sha256: string): Promise<{ id: string; filename: string; createdAt: string } | null>;
  createImport(row: {
    filename: string;
    content_sha256: string;
    account_iban: string | null;
    statement_number: string | null;
    statement_date: string | null;
    movement_count: number;
    problems: string[];
    format?: 'coda' | 'csv';
    period_from?: string | null;
    period_to?: string | null;
  }): Promise<string>;
  updateImportCounts(id: string, counts: { imported_count: number; duplicate_count: number }): Promise<void>;

  /**
   * Les références bancaires déjà connues, parmi celles proposées.
   *
   * Pour le relevé (`bank`), « connue » veut dire : écrite par un relevé, OU
   * portée comme `coda_ref` par une ligne de l'export CSV qui décrit le même
   * mouvement. `external_ref` rend alors la référence du relevé, et `id` la
   * ligne CSV.
   */
  findKnownPayments(externalRefs: string[], source?: SourceBancaire): Promise<{ external_ref: string; id: string; allocated_cents: number }[]>;

  /**
   * Les remboursements déjà connus, parmi ceux proposés.
   *
   * Le symétrique de `findKnownPayments`, et il manquait. `refunds` porte
   * pourtant `UNIQUE (source, external_ref)` : sans ce filtre, `insertRefunds`
   * lève sur le premier doublon — **après** que les paiements et les
   * allocations ont été écrits, et avant `updateImportCounts`. L'écran annonce
   * alors « 0 paiement enregistré » alors que tout est en base.
   *
   * Mesuré sur un fichier CODA d'un an : 46 remboursements candidats, dont
   * **45 déjà en base**.
   */
  findKnownRefunds(externalRefs: string[], source?: SourceBancaire): Promise<{
    external_ref: string;
    id: string;
    /* Ce que la ligne porte DEJA. Sans ces trois champs, une relecture ne peut
       pas savoir ce qui lui manque, et completer devient ecraser. */
    counterparty_iban?: string | null;
    counterparty_name?: string | null;
    free_text?: string | null;
  }[]>;

  /** Les factures dont les 7 chiffres figurent dans la liste. */
  /** Une référence est soit 7 chiffres, soit un numéro entier du format historique. */
  loadInvoicesByDigits(references: string[]): Promise<InvoiceRef[]>;
  /** Les factures portant l'une de ces communications structurées. */
  loadInvoicesByStructured(digits: string[]): Promise<InvoiceRef[]>;

  /**
   * Combien de mouvements la base porte deja, par forme, sur la periode.
   *
   * La cle est celle de `cleDeMouvement` : date valeur, sens, montant, compte
   * de contrepartie. C'est ce comptage, et non une egalite d'identifiant, qui
   * permet a un CSV de combler un trou sans doubler un CODA — les deux formats
   * n'ont aucun identifiant commun.
   */
  mouvementsConnus(du: string, au: string): Promise<Record<string, number>>;

  /**
   * Les lignes de l'export CSV pas encore reliées à un relevé, sur la période.
   *
   * Ce qu'un CODA de cette période pourrait décrire une seconde fois. Voir
   * `jumeaux.ts` et la migration `20260927144800`.
   */
  mouvementsCsvARelier(du: string, au: string): Promise<LigneCsv[]>;

  /**
   * Pose la référence du relevé sur des lignes de l'export CSV — tout ou rien.
   *
   * La base recompare date, montant et compte avant d'écrire, et refuse une
   * ligne déjà reliée. Rend le nombre de lignes reliées.
   */
  relierAuCoda(liens: LienCoda[]): Promise<number>;

  insertPayments(importId: string, rows: PaymentRow[]): Promise<Map<string, string>>;
  insertAllocations(rows: {
    payment_id: string;
    amount_cents: number;
    target: 'invoice' | 'out_of_scope';
    invoice_id: string | null;
    method: string;
    notes?: string | null;
  }[]): Promise<number>;
  insertRefunds(importId: string, rows: RefundRow[]): Promise<number>;

  /**
   * Complète des remboursements déjà en base, sans jamais rien remplacer.
   *
   * Rend le nombre de lignes réellement modifiées.
   */
  completeRefunds(rows: RefundCompletion[]): Promise<number>;
}

/** Empreinte SHA-256 du contenu, en hexadécimal. */
export async function contentHash(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as unknown as ArrayBuffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Le nom du donneur d'ordre, reconstitué depuis les enregistrements 31 et 32. */
function counterpartyName(m: CodaMovement): string | null {
  const t = m.freeText.trim();
  return t.length > 0 ? t.slice(0, 120) : null;
}

function toPaymentRow(m: CodaMovement, source: SourceBancaire = 'bank'): PaymentRow {
  const c = m.communication;
  return {
    source,
    external_ref: m.reference,
    amount_cents: m.amountCents,
    value_date: m.valueDate,
    booking_date: m.entryDate ?? null,
    communication_raw: c.raw.trim() || null,
    communication_kind: c.kind,
    structured_digits: c.structuredDigits ?? null,
    legacy_invoice_ref: c.legacyInvoiceRef ?? null,
    possibly_truncated: c.possiblyTruncated,
    counterparty_iban: m.counterpartyIban ?? null,
    // Le nom vient de l'enregistrement 23 quand il existe, du texte libre
    // sinon : c'est ce nom que l'administration reconnaît quand la
    // communication ne dit rien.
    counterparty_name: m.counterpartyName ?? counterpartyName(m),
    // Conservé en entier : c'est la seule copie de ce que le parent a écrit
    // quand la communication a été coupée à 53 caractères.
    free_text: m.freeText.trim() || null,
  };
}

/**
 * Les virements sortants qui sont des remboursements.
 *
 * **On lit TOUS les virements sortants, et on classe ensuite.**
 *
 * L'ancienne regle ne retenait un debit que s'il citait une facture ou une note
 * de credit. Mesure sur le fichier CODA d'un an, le 14 septembre 2026 :
 *
 * | | mouvements | montant |
 * |---|---|---|
 * | virements sortants du releve | 1 758 | 1 171 363,71 EUR |
 * | retenus par l'ancienne regle | 46 | 3 215,20 EUR |
 * | **jetes sans laisser de trace** | **1 712** | **1 168 148,51 EUR** |
 *
 * Trois d'entre eux citaient pourtant `CDV` sans etre lus, dont un vrai
 * remboursement : `CdV-250602-00070 Remboursement double paiement`, 180,00 EUR,
 * au format ancien que `invoiceDigits` ne sait pas reduire.
 *
 * L'entrant avait deja tranche la question dans l'autre sens, et pour une
 * raison chiffree : `incomingPayments` ne filtre rien, et `planOne` classe
 * apres coup. **Lire n'est pas rattacher** — c'est la posture d'`out_of_scope`,
 * et elle vaut dans les deux sens.
 *
 * Ce que l'on gagne : le volontariat et les salaires apparaissent CLASSES au
 * lieu de disparaitre, le total debit du releve devient rapprochable, et les
 * 337 virements qui ne citent rien mais portent un beneficiaire — fournisseurs,
 * CPAS, compensations aux ecoles, remboursements de notes de frais — deviennent
 * visibles et rattachables a la main.
 */


/**
 * Les depenses du releve, et leur idempotence.
 *
 * Extraite de `planImport` parce qu'elle est appelee sur DEUX chemins : l'import
 * ordinaire, et la **reprise d'un fichier deja importe**.
 */
/**
 * Ce qu'une relecture ajoute a une ligne deja en base — ou rien.
 *
 * Le sens unique est la regle : un champ vide en base et rempli dans le relevé
 * est ecrit ; tout le reste est laisse tel quel. Rend `null` quand il n'y a
 * rien a ajouter, pour que l'appelant n'ecrive pas une mise a jour vide.
 */
export function completerLeRemboursement(
  connu: {
    id: string;
    external_ref: string;
    counterparty_iban?: string | null;
    counterparty_name?: string | null;
    free_text?: string | null;
  },
  m: CodaMovement,
): RefundCompletion | null {
  const complement: RefundCompletion = { id: connu.id, external_ref: connu.external_ref };
  let quelqueChose = false;

  const vide = (v: string | null | undefined) => v === null || v === undefined || v.trim() === '';

  const iban = m.counterpartyIban?.trim();
  if (iban && vide(connu.counterparty_iban)) {
    complement.counterparty_iban = iban;
    quelqueChose = true;
  }

  /* Le nom de l'enregistrement 23 s'il existe, du texte libre sinon — la meme
     regle qu'a l'insertion, sinon une relecture ecrirait un nom different de
     celui qu'un premier import aurait ecrit pour le meme mouvement. */
  const nom = (m.counterpartyName ?? counterpartyName(m))?.trim();
  if (nom && vide(connu.counterparty_name)) {
    complement.counterparty_name = nom;
    quelqueChose = true;
  }

  const texte = m.freeText.trim();
  if (texte && vide(connu.free_text)) {
    complement.free_text = texte;
    quelqueChose = true;
  }

  return quelqueChose ? complement : null;
}

/** Ce que l'appariement lit d'un mouvement du relevé. */
function formeDuMouvement(m: CodaMovement) {
  return {
    reference: m.reference,
    valueDate: m.valueDate,
    amountCents: m.amountCents,
    counterpartyIban: m.counterpartyIban ?? null,
    structuredDigits: m.communication.structuredDigits ?? null,
    communicationRaw: m.communication.raw.trim() || null,
    mouvement: m,
  };
}

/**
 * Apparie des mouvements du relevé avec les lignes CSV de leur sens, et note
 * dans le plan ce qui sera relié et ce qui est douteux. Rend les références
 * reliées, avec leur ligne CSV : l'appelant ne les écrit pas.
 *
 * Une ligne CSV reliée ici ne peut plus servir à un autre mouvement du même
 * fichier : elle est retirée de `lignesCsv`.
 */
function noterLesJumeaux(
  sens: Sens,
  mouvements: CodaMovement[],
  lignesCsv: LigneCsv[],
  plan: ImportPlan,
): Map<string, LigneCsv> {
  const candidates = lignesCsv.filter((l) => l.sens === sens);
  const { reunis, probables } = apparierAvecLExportCsv(mouvements.map(formeDuMouvement), candidates);
  const reliees = new Map<string, LigneCsv>();

  for (const a of reunis) {
    const m = a.mouvement.mouvement;
    plan.reunisAuCsv.push({
      sens,
      externalRef: m.reference,
      csvId: a.ligne.id,
      valueDate: m.valueDate,
      amountCents: m.amountCents,
      counterpartyIban: m.counterpartyIban ?? null,
      counterpartyName: m.counterpartyName ?? counterpartyName(m),
      freeText: m.freeText.trim() || null,
      communications: a.communications === 'identiques' ? 'identiques' : 'une_absente',
      communication: a.mouvement.communicationRaw,
    });
    reliees.set(m.reference, a.ligne);
    const i = lignesCsv.indexOf(a.ligne);
    if (i >= 0) lignesCsv.splice(i, 1);
  }
  for (const a of probables) {
    plan.doublonsProbables.push({
      sens,
      externalRef: a.mouvement.reference,
      csvId: a.ligne.id,
      valueDate: a.mouvement.valueDate,
      amountCents: a.mouvement.amountCents,
      communicationReleve: a.mouvement.communicationRaw,
      communicationCsv: a.ligne.communicationRaw ?? null,
    });
  }
  return reliees;
}

/**
 * Les lignes de l'export CSV que ce relevé pourrait décrire une seconde fois.
 *
 * `null` pour un export CSV : lui se reconnaît au comptage
 * (`mouvementsConnus`), et ne se relie pas à lui-même.
 */
async function chargerLesLignesCsv(plan: ImportPlan, gateway: ImportGateway): Promise<LigneCsv[] | null> {
  if (plan.source !== 'bank' || !plan.periodFrom || !plan.periodTo) return null;
  return gateway.mouvementsCsvARelier(plan.periodFrom, plan.periodTo);
}

async function planLesDepenses(
  remboursements: CodaMovement[],
  index: { byLegacyDigits(d: string): InvoiceRef | undefined },
  plan: ImportPlan,
  gateway: ImportGateway,
  lignesCsv: LigneCsv[] | null,
): Promise<void> {
  //
  // Le filtre d'idempotence, symetrique de celui des paiements. Il manquait, et
  // son absence faisait lever `insertRefunds` sur le premier doublon — APRES
  // l'ecriture des paiements et des allocations. Voir `findKnownRefunds`.
  const remboursementsConnus = new Map(
    (await gateway.findKnownRefunds(remboursements.map((m) => m.reference), plan.source))
      .map((r) => [r.external_ref, r]),
  );

  /* Ce que l'export CSV a deja ecrit, pour les seules sorties que le releve
     ne connait pas encore par leur reference. Voir `apparierAvecLExportCsv`. */
  const reunies = lignesCsv
    ? noterLesJumeaux('sortie', remboursements.filter((m) => !remboursementsConnus.has(m.reference)), lignesCsv, plan)
    : new Map<string, LigneCsv>();
  /* Une sortie douteuse n'est pas classée hors périmètre d'office : elle reste
     dans la file des sorties, où elle est signalée. */
  const probables = new Set(plan.doublonsProbables.filter((d) => d.sens === 'sortie').map((d) => d.externalRef));

  for (const m of remboursements) {
    const connu = remboursementsConnus.get(m.reference);
    if (connu) {
      plan.duplicateRefundRefs.push(m.reference);
      /* Deja en base, mais peut-etre plus pauvre que ce relevé. Voir
         `RefundCompletion` : on AJOUTE ce qui manque, on ne remplace rien. */
      const complement = completerLeRemboursement(connu, m);
      if (complement) plan.refundCompletions.push(complement);
      continue;
    }
    if (reunies.has(m.reference)) {
      /* Deja ecrite par l'export CSV : reliee, jamais reecrite. La completion
         des champs vides se fait avec le lien (`relier_au_coda`). */
      plan.duplicateRefundRefs.push(m.reference);
      continue;
    }
    const d = m.communication.legacyInvoiceRef ? invoiceDigits(m.communication.legacyInvoiceRef) : undefined;
    const facture = d ? index.byLegacyDigits(d) : undefined;
    plan.refunds.push({
      source: plan.source,
      external_ref: m.reference,
      amount_cents: m.amountCents,
      value_date: m.valueDate,
      booking_date: m.entryDate ?? null,
      communication_raw: m.communication.raw.trim(),
      communication_kind: m.communication.kind,
      structured_digits: m.communication.structuredDigits ?? null,
      legacy_invoice_ref: m.communication.legacyInvoiceRef ?? null,
      invoice_id: facture?.id ?? null,
      possibly_truncated: m.communication.possiblyTruncated,
      creditNoteNumbers: m.communication.creditNoteRefs,
      /* Conserve en entier, comme pour un paiement recu : c'est la seule copie
         de ce que l'administration a ecrit au-dela des 53 caracteres du champ
         communication, et le numero de note est precisement ce qui y saute. */
      free_text: m.freeText.trim() || null,
      counterparty_iban: m.counterpartyIban ?? null,
      /* Le nom de l'enregistrement 23 quand il existe, du texte libre sinon —
         la même règle que pour un paiement reçu. */
      counterparty_name: m.counterpartyName ?? counterpartyName(m),
      /* Ecarte d'office quand la FORME le dit : volontariat, salaire, frais.
         Mesure sur le fichier d'un an : 1 375 des 1 758 sortants, dont 1 027 de
         volontariat et 341 de salaire. Sans ce classement, la file du sortant
         serait noyee et les 337 virements qui demandent vraiment un regard
         seraient introuvables. Le mouvement est ECRIT et CLASSE, jamais jete. */
      out_of_scope: probables.has(m.reference)
        ? null
        : (classifyOutgoing(m.communication.raw) ?? classifyOutgoing(m.freeText))?.category ?? null,
    });
  }
}

/**
 * Une sortie qui n'est pas un virement, telle que les deux formats l'écrivent.
 *
 * La forme dit ce que c'est — un frais de la banque, une opération par carte,
 * une domiciliation… — et jamais un remboursement à une famille : compte,
 * classée hors périmètre, visible, jamais rapprochée.
 */
function sortieHorsVirement(m: CodaMovement, source: SourceBancaire, nom: string | null): RefundRow {
  return {
    source,
    external_ref: m.reference,
    amount_cents: m.amountCents,
    value_date: m.valueDate,
    booking_date: m.entryDate ?? null,
    communication_raw: m.communication.raw.trim(),
    communication_kind: m.communication.kind,
    structured_digits: m.communication.structuredDigits ?? null,
    legacy_invoice_ref: null,
    invoice_id: null,
    possibly_truncated: m.communication.possiblyTruncated,
    creditNoteNumbers: [],
    free_text: m.freeText.trim() || null,
    counterparty_iban: m.counterpartyIban ?? null,
    counterparty_name: nom,
    out_of_scope: m.family === '35' ? 'frais_bancaires'
      : m.family === '04' ? 'operation_carte'
      : 'autre_hors_virement',
  };
}

/**
 * Les opérations du relevé qui ne sont pas des virements.
 *
 * ## Le défaut que ceci ferme (29 septembre 2026)
 *
 * L'import ne retenait que les virements (familles `01` et `02`) : cartes,
 * frais, domiciliations tombaient sans laisser de trace. Tant que l'export CSV,
 * qui les porte, couvrait la période, rien ne se voyait ; après sa fin, le
 * 17/09/2026, **8 opérations** manquaient — l'extrait 268 en comptait 12,
 * l'import 10 — et le solde ING de CDV dépassait de **317,33 €** celui de la
 * banque. En partie double, tout mouvement du compte est une écriture.
 *
 * ## Écrites comme l'export CSV les écrit
 *
 * Une sortie est classée hors périmètre à la forme (`sortieHorsVirement`) ;
 * une entrée (dépôt d'espèces, remboursement par carte) est écrite sans
 * affectation, visible dans la file. Aucune n'est proposée au rapprochement.
 *
 * ## Sans jamais écrire deux fois
 *
 * - **par la référence** : une opération qu'un import précédent a écrite —
 *   ce relevé relu, ou un réexport — est reconnue, comme un virement ;
 * - **au comptage**, contre l'export CSV : les deux formats n'ont aucun
 *   identifiant commun, et l'appariement des virements ne vaut pas ici (une
 *   ligne sans compte n'y entre pas). On compte donc, comme l'export le fait
 *   face au relevé depuis le 18/09 : pour une date valeur, un sens, un montant
 *   et un compte, si la base en porte `n` et le relevé `m`, il en manque
 *   `max(0, m − n)`. Deux frais identiques le même jour restent deux lignes.
 *
 * Les lignes que la base porte déjà par leur référence figurent aussi dans ce
 * comptage : elles en sont retirées d'abord, sinon une opération déjà écrite
 * ferait passer pour connue sa jumelle qui ne l'est pas.
 */
async function planLesOperationsHorsVirement(
  operations: CodaMovement[],
  plan: ImportPlan,
  gateway: ImportGateway,
): Promise<void> {
  for (const m of operations) {
    if (m.amountCents === 0) plan.horsVirementNuls.push(m.reference);
  }
  const horsVirement = operations.filter((m) => m.amountCents !== 0);
  if (horsVirement.length === 0) return;

  const credits = horsVirement.filter((m) => m.direction === 'credit');
  const debits = horsVirement.filter((m) => m.direction === 'debit');
  const creditsConnus = new Set(credits.length
    ? (await gateway.findKnownPayments(credits.map((m) => m.reference), plan.source)).map((p) => p.external_ref)
    : []);
  const debitsConnus = new Set(debits.length
    ? (await gateway.findKnownRefunds(debits.map((m) => m.reference), plan.source)).map((r) => r.external_ref)
    : []);
  const parReference = (m: CodaMovement) =>
    (m.direction === 'credit' ? creditsConnus : debitsConnus).has(m.reference);

  const aCompter = horsVirement.filter((m) => !parReference(m));
  let nouveaux = new Set<CodaMovement>(aCompter);
  if (aCompter.length > 0 && plan.periodFrom && plan.periodTo) {
    const connus = { ...(await gateway.mouvementsConnus(plan.periodFrom, plan.periodTo)) };
    for (const m of horsVirement) {
      if (!parReference(m)) continue;
      const cle = cleDeMouvement(m);
      if ((connus[cle] ?? 0) > 0) connus[cle] -= 1;
    }
    nouveaux = new Set(aEcrire(aCompter, connus).garder);
  }

  for (const m of horsVirement) {
    if (parReference(m)) {
      (m.direction === 'credit' ? plan.duplicateRefs : plan.duplicateRefundRefs).push(m.reference);
    } else if (!nouveaux.has(m)) {
      plan.horsVirementDejaEcrits.push(m.reference);
    } else if (m.direction === 'credit') {
      plan.payments.push(toPaymentRow(m, plan.source));
      plan.horsVirementAEcrire.push(m.reference);
    } else {
      plan.refunds.push(sortieHorsVirement(m, plan.source, m.counterpartyName ?? counterpartyName(m)));
      plan.horsVirementAEcrire.push(m.reference);
    }
  }
}

/**
 * Chaque mouvement du relevé finit dans UN panier : écrit, connu par sa
 * référence, relié à l'export CSV, reconnu au comptage, nul — ou, pour un fichier
 * déjà importé, protégé par son empreinte (ses virements reçus). Un mouvement
 * dans aucun panier est un mouvement perdu : il est nommé dans `problems`,
 * jamais avalé en silence. C'est ce contrôle qui aurait vu les 8 opérations
 * du 29/09/2026.
 */
export function verifierQueRienNeSePerd(plan: ImportPlan, virementsRecusProteges: number): void {
  const couverts = plan.payments.length + plan.duplicateRefs.length
    + plan.refunds.length + plan.duplicateRefundRefs.length
    + plan.horsVirementDejaEcrits.length + plan.horsVirementNuls.length + virementsRecusProteges;
  if (couverts !== plan.movementCount) {
    plan.problems.push(
      `mouvements : ${plan.movementCount} dans le relevé, ${couverts} écrits ou reconnus — `
      + `${plan.movementCount - couverts} ne seraient ni écrits ni reconnus`,
    );
  }
}

export async function planImport(
  input: Uint8Array | string,
  filename: string,
  gateway: ImportGateway,
): Promise<ImportPlan> {
  const bytes = typeof input === 'string' ? new TextEncoder().encode(input) : input;
  const sha256 = await contentHash(bytes);
  const statement = parseCoda(typeof input === 'string' ? input : decodeCodaBytes(bytes));

  const recus = incomingPayments(statement);
  /* TOUS les virements sortants, et non les seuls qui citent quelque chose.
     Voir le commentaire ci-dessus : l'ancienne regle en jetait 1 712 sur 1 758. */
  const remboursements = outgoingTransfers(statement);
  /* Et tout ce qui n'est pas un virement : jete jusqu'au 29/09/2026. */
  const horsVirement = nonTransfers(statement);

  const dates = statement.movements.map((m) => m.valueDate).sort();
  const plan: ImportPlan = {
    filename,
    contentSha256: sha256,
    format: 'coda',
    source: 'bank',
    periodFrom: dates[0] ?? null,
    periodTo: dates[dates.length - 1] ?? null,
    dejaConnus: 0,
    alreadyImportedAs: await gateway.findImportByHash(sha256),
    accountIban: statement.accountIban,
    statementNumber: statement.statementNumber,
    statementDate: recus[0]?.valueDate ?? statement.movements[0]?.valueDate ?? null,
    problems: statement.problems,
    movementCount: statement.movements.length,
    payments: [],
    duplicateRefs: [],
    duplicateRefundRefs: [],
    refundCompletions: [],
    reunisAuCsv: [],
    doublonsProbables: [],
    horsVirementDejaEcrits: [],
    horsVirementNuls: [],
    horsVirementAEcrire: [],
    soldes: { ancienCents: statement.oldBalanceCents, nouveauCents: statement.newBalanceCents },
    allocations: [],
    refunds: [],
    matchPlans: [],
    summary: summarize([]),
  };

  /* Ce que l'export CSV a deja ecrit sur la periode du releve. Lu une fois,
     pour les entrees comme pour les sorties. */
  const lignesCsv = await chargerLesLignesCsv(plan, gateway);

  /*
    LA REPRISE DES DEPENSES D'UN FICHIER DEJA IMPORTE.

    Jusqu'au 14 septembre 2026, les virements sortants etaient jetes : sur le
    fichier CODA d'un an, 1 712 des 1 758 n'ont laisse aucune trace. Un fichier
    deja importe n'a donc PAS livre ses depenses, et la garde par empreinte —
    qui protege les PAIEMENTS d'etre ecrits deux fois — les condamnerait a ne
    jamais l'etre.

    Or l'idempotence des depenses ne repose pas sur l'empreinte : elle repose
    sur `findKnownRefunds` et sur `UNIQUE (source, external_ref)`. On peut donc
    les reprendre sans aucun risque, et sans toucher a un seul paiement.

    Concretement : reimporter le meme fichier n'ecrit plus rien cote entrant —
    `plan.payments` reste vide — mais rattrape tout le sortant.
  */
  if (plan.alreadyImportedAs) {
    /* Un index reduit, bati sur les seules references citees par les depenses :
       il sert uniquement a resoudre `invoice_id`, et charger l'index complet
       serait payer le rapprochement entrant qu'on ne fera pas. */
    const chiffresDesDepenses = new Set<string>();
    for (const m of remboursements) {
      const d = m.communication.legacyInvoiceRef ? invoiceDigits(m.communication.legacyInvoiceRef) : undefined;
      if (d) chiffresDesDepenses.add(d);
    }
    const facturesCitees = chiffresDesDepenses.size
      ? await gateway.loadInvoicesByDigits([...chiffresDesDepenses])
      : [];
    await planLesDepenses(remboursements, buildInvoiceIndex(facturesCitees), plan, gateway, lignesCsv);
    /* Les operations hors virement, jetees jusqu'au 29/09/2026 : un fichier
       deja importe ne les a pas livrees. Leur idempotence ne repose pas sur
       l'empreinte non plus — reference, puis comptage — : on les reprend. */
    await planLesOperationsHorsVirement(horsVirement, plan, gateway);
    verifierQueRienNeSePerd(plan, recus.length);
    return plan;
  }

  await planDepuisMouvements(recus, remboursements, plan, gateway, lignesCsv);
  await planLesOperationsHorsVirement(horsVirement, plan, gateway);
  verifierQueRienNeSePerd(plan, 0);
  return plan;
}

/**
 * Le rapprochement et l'ecriture projetee, a partir de mouvements deja lus.
 *
 * Extraite de `planImport` pour servir les DEUX formats. Ce qui differe entre
 * un CODA et un CSV est la lecture du fichier et la facon de reconnaitre un
 * mouvement deja connu ; tout ce qui suit — echelons de confiance, file
 * manuelle, classement hors perimetre, depenses — est le meme travail, et un
 * second moteur serait un second endroit ou se tromper.
 */
async function planDepuisMouvements(
  recus: CodaMovement[],
  remboursements: CodaMovement[],
  plan: ImportPlan,
  gateway: ImportGateway,
  lignesCsv: LigneCsv[] | null = null,
): Promise<ImportPlan> {
  // --- Ce qui existe déjà -----------------------------------------------------
  const connus = new Map(
    (await gateway.findKnownPayments(recus.map((m) => m.reference), plan.source)).map((p) => [p.external_ref, p]),
  );

  /*
    Ce que l'export CSV a déjà écrit, et que le relevé ne connaît pas encore
    par sa référence. Un jumeau sûr devient un paiement CONNU : il n'est pas
    réécrit, et s'il n'est encore placé nulle part, il est repris dans le
    rapprochement comme n'importe quel paiement connu non affecté — ses
    allocations iront sur la ligne CSV, que `findKnownPayments` retrouve par
    son `coda_ref` une fois le lien posé.
  */
  if (lignesCsv) {
    const inconnus = recus.filter((m) => !connus.has(m.reference));
    for (const [ref, ligne] of noterLesJumeaux('entree', inconnus, lignesCsv, plan)) {
      connus.set(ref, { external_ref: ref, id: ligne.id, allocated_cents: ligne.allocatedCents });
    }
  }

  for (const m of recus) {
    if (connus.has(m.reference)) plan.duplicateRefs.push(m.reference);
    else plan.payments.push(toPaymentRow(m, plan.source));
  }

  // Un paiement déjà en base mais **jamais affecté** est repris dans le
  // rapprochement. C'est le cas d'un import précédent interrompu entre la
  // création des paiements et celle des allocations : sans cela, le réimporter
  // ne réparerait rien puisque tout serait vu comme doublon.
  //
  // Un doublon PROBABLE, lui, n'est jamais placé d'office : il est écrit, et
  // il attend dans la file qu'un humain dise si c'est le même virement. Le
  // placer automatiquement le ferait sortir de la file — et le signalement
  // promis ne serait plus nulle part.
  const probables = new Set(plan.doublonsProbables.filter((d) => d.sens === 'entree').map((d) => d.externalRef));
  const aRapprocher = recus.filter((m) => {
    if (probables.has(m.reference)) return false;
    const connu = connus.get(m.reference);
    return !connu || connu.allocated_cents === 0;
  });

  // --- L'index des factures, chargé pour les seules références lues ----------
  const chiffres = new Set<string>();
  const structurees = new Set<string>();
  for (const m of aRapprocher) {
    /* Le numéro ENTIER quand la référence est au format historique : ses sept
       derniers chiffres ne se trouvent pas dans le numéro brut, un tiret les
       coupe. */
    for (const d of allLegacyLookups(m.communication.raw)) chiffres.add(d);
    if (m.communication.structuredDigits) structurees.add(m.communication.structuredDigits);
  }
  for (const m of remboursements) {
    const d = m.communication.legacyInvoiceRef ? invoiceDigits(m.communication.legacyInvoiceRef) : undefined;
    if (d) chiffres.add(d);
  }

  const factures = [
    ...(chiffres.size ? await gateway.loadInvoicesByDigits([...chiffres]) : []),
    ...(structurees.size ? await gateway.loadInvoicesByStructured([...structurees]) : []),
  ];
  const index = buildInvoiceIndex(factures);

  // --- Le rapprochement -------------------------------------------------------
  plan.matchPlans = planBatch(aRapprocher, index);
  plan.summary = summarize(plan.matchPlans);

  for (const p of plan.matchPlans) {
    for (const a of p.allocations) {
      plan.allocations.push({
        externalRef: p.externalRef,
        amount_cents: a.amountCents,
        target: 'invoice',
        invoice_id: a.invoiceId,
        method: a.method,
      });
    }

    /*
      La part hors périmètre, écrite comme le sortant l'écrit depuis toujours.

      Elle vaut le montant entier quand la communication ne parle que d'une
      autre comptabilité, et le seul RESTE quand elle cite aussi une facture
      d'ici. Le mouvement demeure entier en base ; c'est son affectation qui dit
      que cet argent n'appartient pas aux centres de vacances, et elle se défait
      d'un geste depuis la file.
    */
    if (p.outOfScope && (p.outOfScopeCents ?? 0) > 0) {
      plan.allocations.push({
        externalRef: p.externalRef,
        amount_cents: p.outOfScopeCents as number,
        target: 'out_of_scope',
        invoice_id: null,
        method: 'manual',
        notes: `Reconnu a la forme : ${p.outOfScope.label} (${p.outOfScope.matched})`,
      });
    }
  }

  await planLesDepenses(remboursements, index, plan, gateway, lignesCsv);

  return plan;
}

/**
 * Écrit le plan.
 *
 * L'ordre compte : le relevé d'import d'abord, pour que les paiements puissent
 * s'y rattacher, puis les paiements, puis les allocations qui ont besoin de
 * leurs identifiants.
 *
 * Les allocations dont le paiement n'a pas pu être créé — un doublon repris
 * pour rapprochement, dont l'identifiant vient de la base et non de l'insertion
 * — sont résolues via la table des paiements connus.
 */
export async function commitImport(plan: ImportPlan, gateway: ImportGateway): Promise<ImportReport> {
  /*
    Les liens d'abord, avant toute écriture.

    Ils disent « ce mouvement du relevé, l'export CSV l'a déjà écrit ». Posés en
    premier, et tout ou rien : si la base en refuse un — une autre session l'a
    relié entre l'analyse et l'écriture, ou la ligne CSV a changé —, l'import
    s'arrête AVANT d'avoir écrit quoi que ce soit, au lieu d'écrire la moitié
    d'un relevé. Et c'est par eux que `findKnownPayments` retrouve ensuite la
    ligne CSV d'un paiement repris pour rapprochement.
  */
  const reunisAuCsv = await relierLesJumeaux(plan, gateway);

  if (plan.alreadyImportedAs) {
    /*
      Le fichier est deja importe : aucun PAIEMENT n'est ecrit, et c'est ce que
      la garde par empreinte protege.

      Mais ses DEPENSES, elles, peuvent manquer — jusqu'au 14 septembre 2026
      elles etaient jetees, 1 712 sur 1 758 sur le fichier d'un an. On les ecrit
      donc, rattachees a l'import d'origine pour que le journal reste juste.

      Sans risque : `planLesDepenses` a deja retire celles que
      `findKnownRefunds` connait, et `UNIQUE (source, external_ref)` refuserait
      ce qui passerait quand meme.
    */
    const reprises = plan.refunds.length
      ? await gateway.insertRefunds(plan.alreadyImportedAs.id, plan.refunds)
      : 0;

    /*
      Les seules ENTREES qu'un fichier deja importe peut encore livrer : ses
      operations hors virement (depot d'especes, remboursement par carte),
      jetees jusqu'au 29/09/2026. `planImport` ne met rien d'autre dans
      `payments` sur ce chemin — ses virements recus restent proteges par
      l'empreinte —, et `UNIQUE (source, external_ref)` refuserait le reste.
    */
    const entreesReprises = plan.payments.length
      ? (await gateway.insertPayments(plan.alreadyImportedAs.id, plan.payments)).size
      : 0;

    /*
      Et c'est ICI que la relecture prend son sens. Ce chemin est celui d'un
      fichier deja importe : rien de neuf a ecrire, mais les lignes qu'il decrit
      peuvent etre plus pauvres en base que dans le fichier. Demande le 20
      septembre 2026 pour les 34 remboursements sans compte de beneficiaire.
    */
    const completes = plan.refundCompletions.length
      ? await gateway.completeRefunds(plan.refundCompletions)
      : 0;

    return {
      importId: plan.alreadyImportedAs.id,
      alreadyImported: true,
      paymentsCreated: entreesReprises,
      duplicatesSkipped: plan.duplicateRefs.length,
      refundDuplicatesSkipped: plan.duplicateRefundRefs.length,
      allocationsCreated: 0,
      refundsCreated: reprises,
      refundsCompleted: completes,
      reunisAuCsv,
      summary: plan.summary,
    };
  }

  const importId = await gateway.createImport({
    filename: plan.filename,
    content_sha256: plan.contentSha256,
    account_iban: plan.accountIban || null,
    statement_number: plan.statementNumber || null,
    statement_date: plan.statementDate,
    movement_count: plan.movementCount,
    problems: plan.problems,
    format: plan.format,
    period_from: plan.periodFrom,
    period_to: plan.periodTo,
  });

  const idParRef = plan.payments.length
    ? await gateway.insertPayments(importId, plan.payments)
    : new Map<string, string>();

  // Les paiements déjà en base, repris pour rapprochement.
  if (plan.duplicateRefs.length) {
    for (const p of await gateway.findKnownPayments(plan.duplicateRefs)) {
      idParRef.set(p.external_ref, p.id);
    }
  }

  const lignes = plan.allocations
    .map((a) => {
      const paymentId = idParRef.get(a.externalRef);
      return paymentId
        ? {
            payment_id: paymentId,
            amount_cents: a.amount_cents,
            target: a.target,
            invoice_id: a.invoice_id,
            method: a.method,
            notes: a.notes ?? null,
          }
        : null;
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  const allocationsCreated = lignes.length ? await gateway.insertAllocations(lignes) : 0;
  const refundsCreated = plan.refunds.length ? await gateway.insertRefunds(importId, plan.refunds) : 0;
  /* Un fichier neuf peut lui aussi decrire des mouvements deja connus par une
     AUTRE source — un CSV apres un CODA, ou l'inverse. */
  const refundsCompleted = plan.refundCompletions.length
    ? await gateway.completeRefunds(plan.refundCompletions)
    : 0;

  await gateway.updateImportCounts(importId, {
    imported_count: plan.payments.length,
    duplicate_count: plan.duplicateRefs.length,
  });

  return {
    importId,
    alreadyImported: false,
    paymentsCreated: plan.payments.length,
    duplicatesSkipped: plan.duplicateRefs.length,
    refundDuplicatesSkipped: plan.duplicateRefundRefs.length,
    refundsCompleted,
    allocationsCreated,
    refundsCreated,
    reunisAuCsv,
    summary: plan.summary,
  };
}

/** Pose les liens du plan, et refuse de continuer si la base ne les a pas tous pris. */
async function relierLesJumeaux(plan: ImportPlan, gateway: ImportGateway): Promise<number> {
  if (plan.reunisAuCsv.length === 0) return 0;
  const liens: LienCoda[] = plan.reunisAuCsv.map((r) => ({
    sens: r.sens,
    id: r.csvId,
    coda_ref: r.externalRef,
    value_date: r.valueDate,
    amount_cents: r.amountCents,
    counterparty_iban: r.counterpartyIban,
    counterparty_name: r.counterpartyName,
    free_text: r.freeText,
  }));
  const n = await gateway.relierAuCoda(liens);
  if (n !== liens.length) {
    throw new Error(
      `${liens.length - n} mouvement(s) déjà écrits par l'export CSV n'ont pas pu être reliés au relevé : rien n'a été importé. Relancez l'analyse du fichier.`,
    );
  }
  return n;
}

/* -------------------------------------------------------------------------- */
/* L'export CSV de la banque                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Projette l'import d'un export CSV, en ne gardant que ce qui manque.
 *
 * ## Ce qui change par rapport à un CODA, et ce qui ne change pas
 *
 * **Change** : la lecture du fichier, et la façon de reconnaître un mouvement
 * déjà connu. Un CODA porte une référence bancaire de 21 caractères ; un CSV
 * porte un rang dans l'export, qui change d'un export à l'autre. Aucune égalité
 * d'identifiant n'est possible entre les deux, donc on **compte** : pour une
 * date valeur, un sens, un montant et un compte de contrepartie donnés, si la
 * base en porte `n` et le fichier `m`, il en manque `max(0, m − n)`.
 *
 * **Ne change pas** : tout le reste. Même rapprochement, mêmes échelons de
 * confiance, même file manuelle, même classement hors périmètre, mêmes tables.
 *
 * ## Le fichier déjà importé n'est PAS écarté
 *
 * Un CODA déjà importé ne réécrit aucun paiement — l'empreinte le protège. Un
 * CSV, lui, se rejoue : c'est même le geste normal quand on réimporte un export
 * plus large, ou après avoir enfin reçu les CODA manquants. Ce qui protège des
 * doublons n'est pas l'empreinte, c'est le comptage — et il protège aussi bien
 * au premier passage qu'au dixième.
 *
 * ## Les frais et les cartes entrent, sans jamais être rapprochés
 *
 * `isTransfer` ne vaut que pour les virements de compte à compte portant un
 * IBAN. Le reste — frais bancaires, opérations par carte, dépôt d'espèces — est
 * écrit comme mouvement et laissé hors du rapprochement : pour une
 * comptabilité, chacun est une écriture ; pour le circuit des familles, aucun
 * n'a de contrepartie à trouver.
 */
export async function planImportCsv(
  input: Uint8Array | string,
  filename: string,
  gateway: ImportGateway,
): Promise<ImportPlan> {
  const bytes = typeof input === 'string' ? new TextEncoder().encode(input) : input;
  const sha256 = await contentHash(bytes);
  const statement = parseCsvIng(input);

  /* Ce que la base porte déjà sur la période couverte. Un seul appel : la
     réponse est un objet, pas un ensemble de lignes, et ne peut donc pas être
     tronquée en silence à mille. */
  const connus = await gateway.mouvementsConnus(statement.periodFrom, statement.periodTo);
  const { garder, deja } = aEcrire(statement.movements, connus);

  const plan: ImportPlan = {
    filename,
    contentSha256: sha256,
    format: 'csv',
    source: 'bank_csv',
    periodFrom: statement.periodFrom,
    periodTo: statement.periodTo,
    dejaConnus: deja,
    /* Renseigné pour l'affichage — « vous avez déjà envoyé ce fichier » — mais
       il ne coupe rien : le comptage suffit, et rejouer est le geste normal. */
    alreadyImportedAs: await gateway.findImportByHash(sha256),
    accountIban: statement.accountIban,
    statementNumber: '',
    statementDate: statement.periodFrom,
    problems: statement.problems,
    movementCount: statement.movements.length,
    payments: [],
    duplicateRefs: [],
    duplicateRefundRefs: [],
    refundCompletions: [],
    reunisAuCsv: [],
    doublonsProbables: [],
    horsVirementDejaEcrits: [],
    horsVirementNuls: [],
    horsVirementAEcrire: [],
    soldes: null,
    allocations: [],
    refunds: [],
    matchPlans: [],
    summary: summarize([]),
  };

  const recus = garder.filter((m) => m.isTransfer && m.direction === 'credit');
  const sorties = garder.filter((m) => m.isTransfer && m.direction === 'debit');

  /*
    Ce qui n'est pas un virement de compte à compte : frais, cartes, dépôt
    d'espèces. Écrit du bon côté du journal selon son sens, classé hors
    périmètre familles, jamais proposé au rapprochement.
  */
  const horsVirement = garder.filter((m) => !m.isTransfer);
  for (const m of horsVirement.filter((x) => x.direction === 'credit')) {
    plan.payments.push(toPaymentRow(m, plan.source));
  }

  await planDepuisMouvements(recus, sorties, plan, gateway);

  for (const m of horsVirement.filter((x) => x.direction === 'debit')) {
    /* La forme le dit : ce n'est pas un remboursement a une famille, c'est un
       frais de la banque ou une operation par carte. Compte, classe, visible.
       La meme ecriture que pour un releve CODA. */
    plan.refunds.push(sortieHorsVirement(m, plan.source, m.counterpartyName ?? null));
  }

  return plan;
}
